import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { ConfigProvider, App as AntApp, Spin, theme, Result, Button, Typography } from 'antd'
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useRouteError,
  isRouteErrorResponse,
  useParams,
} from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { lazy, Suspense, useMemo, useEffect } from 'react'
import { MindMapFlow } from './components/Mindmap/MindMapFlow'
import { AuthProvider, useAuthContext } from './context/auth'
import { ProfileProvider, useProfileContext } from './context/profile'
import { FeatureAccessProvider } from './context/featureAccess'
import { ThemeProvider, useThemeContext } from './context/theme'
import { IntegrationProvider } from './context/integration'
import { NotificationProvider } from './context/notifications'
import { SaveQueueProvider } from './context/SaveQueueContext'
import { ReadOnlyProvider } from './context/readOnly'
import { DataAccessProvider } from './context/dataAccess'
import { LoginPage } from './Pages/LoginPage'
import { SurveyPage } from './Pages/SurveyPage'
import { SettingsPage } from './Pages/SettingsPage'
import { DashboardPage } from './Pages/DashboardPage'
import { TrelloCallbackPage } from './Pages/TrelloCallbackPage'
import { ReportPage } from './Pages/ReportPage'
import { ValidationPlannerPage } from './Pages/ValidationPlannerPage'
import { UpgradePage } from './Pages/UpgradePage'
import { UpgradeSuccessPage } from './Pages/UpgradeSuccessPage'
import { NotFoundPage } from './Pages/NotFoundPage'
import { SurveyParticipantPage, SurveyInsightsPage } from './features/survey'
import {
  InterviewInsightsPage,
  InterviewConductPage,
  InterviewTranscriptPage,
} from './features/interview'
import { ResearchPage } from './Pages/ResearchPage'
import { SurveyBuilderPage } from './Pages/SurveyBuilderPage'
import { InterviewBuilderPage } from './Pages/InterviewBuilderPage'
import { TermsPage } from './Pages/TermsPage'
import { PrivacyPage } from './Pages/PrivacyPage'
import { PartnerTermsPage } from './Pages/PartnerTermsPage'
import { PartnerSignUpPage } from './Pages/PartnerSignUpPage'
import { AppHeader } from './components/Navigation/AppHeader'
import { EmailConfirmationBanner } from './components/Auth/EmailConfirmationBanner'
import { PublicHeader } from './components/Navigation/PublicHeader'
import { RequireAdmin } from './components/Admin/RequireAdmin'
import { RequirePartner } from './components/Partner/RequirePartner'
import { LoadingContainer, AppLayout as StyledAppLayout, AppContent } from './styles/AppStyles'
import { Z_INDEX, COLORS, TYPOGRAPHY } from './styles/theme'
import { RouteAwareSkeleton } from './features/skeleton'
import { analytics } from './utils/analytics'

// Lazy load admin components (separate bundle)
const AdminLayout = lazy(() =>
  import('./components/Admin/AdminLayout').then((m) => ({ default: m.AdminLayout }))
)
const AdminDashboard = lazy(() =>
  import('./Pages/Admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard }))
)
const UsersPage = lazy(() =>
  import('./Pages/Admin/UsersPage').then((m) => ({ default: m.UsersPage }))
)
const UserDetailPage = lazy(() =>
  import('./Pages/Admin/UserDetailPage').then((m) => ({ default: m.UserDetailPage }))
)
const FeedbackPage = lazy(() =>
  import('./Pages/Admin/FeedbackPage').then((m) => ({ default: m.FeedbackPage }))
)
const ConfigurationsPage = lazy(() =>
  import('./Pages/Admin/ConfigurationsPage').then((m) => ({ default: m.ConfigurationsPage }))
)
const AIGenerationFeedbackPage = lazy(() =>
  import('./Pages/Admin/AIGenerationFeedbackPage').then((m) => ({
    default: m.AIGenerationFeedbackPage,
  }))
)
const TokenAveragesPage = lazy(() =>
  import('./Pages/Admin/TokenAveragesPage').then((m) => ({
    default: m.TokenAveragesPage,
  }))
)
const CreditSystemPage = lazy(() =>
  import('./Pages/Admin/CreditSystemPage').then((m) => ({
    default: m.CreditSystemPage,
  }))
)
const PartnersPage = lazy(() =>
  import('./Pages/Admin/PartnersPage').then((m) => ({
    default: m.PartnersPage,
  }))
)
const FeatureTogglesPage = lazy(() =>
  import('./Pages/Admin/FeatureTogglesPage').then((m) => ({
    default: m.FeatureTogglesPage,
  }))
)

// Lazy load partner components (separate bundle)
const PartnerLayout = lazy(() =>
  import('./components/Partner/PartnerLayout').then((m) => ({ default: m.PartnerLayout }))
)
const PartnerDashboard = lazy(() =>
  import('./Pages/Partner/PartnerDashboard').then((m) => ({ default: m.PartnerDashboard }))
)
const PartnerApplicationPending = lazy(() =>
  import('./Pages/Partner/PartnerApplicationPending').then((m) => ({
    default: m.PartnerApplicationPending,
  }))
)

function getThemeConfig(isDark: boolean) {
  return {
    token: {
      colorPrimary: COLORS.primary,
      borderRadius: 8,
      wireframe: false,
      // Set z-index base to match our Z_INDEX scale in theme.ts
      zIndexPopupBase: 10000,
    },
    components: {
      // Configure component-specific z-indexes to ensure proper stacking
      // Tooltips should always appear on top of other UI elements
      Tooltip: {
        zIndexPopup: Z_INDEX.tooltip,
      },
      Popover: {
        zIndexPopup: Z_INDEX.popover,
      },
      Modal: {
        zIndexPopupBase: Z_INDEX.modal,
      },
      Dropdown: {
        zIndexPopup: Z_INDEX.dropdown,
      },
      Select: {
        zIndexPopup: Z_INDEX.dropdown,
      },
      DatePicker: {
        zIndexPopup: Z_INDEX.dropdown,
      },
      Popconfirm: {
        zIndexPopup: Z_INDEX.popover,
      },
    },
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
  }
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthContext()

  if (loading) {
    return <RouteAwareSkeleton />
  }

  if (!user) {
    return <Navigate to="/login" />
  }

  return children
}

function RequireSurvey({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useProfileContext()

  if (loading) {
    return <RouteAwareSkeleton />
  }

  if (profile && profile.role === 'user' && profile.survey_user_role === null) {
    return <Navigate to="/survey" replace />
  }

  return children
}

function RequireRegularUser({ children }: { children: React.ReactNode }) {
  const { loading } = useProfileContext()

  if (loading) {
    return <RouteAwareSkeleton />
  }

  // Partners and admins can now access the regular app
  // Their dashboards are accessible via navbar links
  return children
}

function AppLayout() {
  return (
    <StyledAppLayout>
      <AppHeader />
      <EmailConfirmationBanner />
      <AppContent>
        <Outlet />
      </AppContent>
    </StyledAppLayout>
  )
}

function PublicLayout() {
  return (
    <StyledAppLayout>
      <PublicHeader />
      <AppContent>
        <Outlet />
      </AppContent>
    </StyledAppLayout>
  )
}

function MindMapWrapper() {
  const { id } = useParams<{ id: string }>()

  useEffect(() => {
    if (id) analytics.mindmapOpened({ mindmap_id: id })
  }, [id])

  return (
    <SaveQueueProvider>
      <DataAccessProvider mode="authenticated">
        <ReadOnlyProvider>
          <ReactFlowProvider>
            <MindMapFlow />
          </ReactFlowProvider>
        </ReadOnlyProvider>
      </DataAccessProvider>
    </SaveQueueProvider>
  )
}

function PublicMindMapWrapper() {
  return (
    <SaveQueueProvider>
      <DataAccessProvider mode="public">
        <ReadOnlyProvider initialReasons={['public']}>
          <ReactFlowProvider>
            <MindMapFlow />
          </ReactFlowProvider>
        </ReadOnlyProvider>
      </DataAccessProvider>
    </SaveQueueProvider>
  )
}

function RouteErrorElement() {
  const error = useRouteError()

  let errorMessage = 'An unexpected error occurred'
  let errorDetails = ''

  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText || 'Route error'
    errorDetails = error.data?.message ?? ''
  } else if (error instanceof Error) {
    errorMessage = error.message
    errorDetails = error.stack ?? ''
  }

  return (
    <Result
      status="error"
      title="Something went wrong"
      subTitle={errorMessage}
      extra={[
        <Button type="primary" key="retry" onClick={() => window.location.reload()}>
          Reload Page
        </Button>,
        <Button key="back" onClick={() => window.history.back()}>
          Go Back
        </Button>,
      ]}
    >
      {errorDetails && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 16, textAlign: 'left' }}>
          <pre style={{ fontSize: TYPOGRAPHY.caption.fontSize, overflow: 'auto', maxHeight: 200 }}>
            {errorDetails}
          </pre>
        </Typography.Paragraph>
      )}
    </Result>
  )
}

function AdminErrorElement() {
  const error = useRouteError()

  let errorMessage = 'An unexpected error occurred'
  let errorDetails = ''

  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText || 'Route error'
    errorDetails = error.data?.message ?? ''
  } else if (error instanceof Error) {
    errorMessage = error.message
    errorDetails = error.stack ?? ''
  }

  return (
    <Result
      status="error"
      title="Admin Error"
      subTitle={errorMessage}
      extra={[
        <Button type="primary" key="retry" onClick={() => window.location.reload()}>
          Reload Page
        </Button>,
        <Button key="dashboard" onClick={() => (window.location.href = '/app/admin')}>
          Back to Admin Dashboard
        </Button>,
        <Button key="home" onClick={() => (window.location.href = '/app/')}>
          Go to Home
        </Button>,
      ]}
    >
      {errorDetails && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 16, textAlign: 'left' }}>
          <pre style={{ fontSize: TYPOGRAPHY.caption.fontSize, overflow: 'auto', maxHeight: 200 }}>
            {errorDetails}
          </pre>
        </Typography.Paragraph>
      )}
    </Result>
  )
}

// Wrap createBrowserRouter so Sentry can name transactions by route pattern
// (e.g. `/mindmap/:id`) instead of raw URLs.
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

const router = sentryCreateBrowserRouter(
  [
    {
      errorElement: <RouteErrorElement />,
      children: [
        {
          path: '/',
          element: (
            <RequireAuth>
              <RequireSurvey>
                <RequireRegularUser>
                  <AppLayout />
                </RequireRegularUser>
              </RequireSurvey>
            </RequireAuth>
          ),
          children: [
            {
              index: true,
              element: <DashboardPage />,
            },
            {
              path: 'mindmap/:id',
              element: <MindMapWrapper />,
            },
            {
              path: 'mindmap/:id/report',
              element: <ReportPage />,
            },
            {
              path: 'mindmap/:id/validate',
              element: <ValidationPlannerPage />,
            },
            {
              path: 'mindmap/:id/research',
              element: <ResearchPage />,
            },
            {
              path: 'survey/:nodeId/insights',
              element: <SurveyInsightsPage />,
            },
            {
              path: 'interview/:nodeId/insights',
              element: <InterviewInsightsPage />,
            },
            {
              path: 'interview/:nodeId/conduct',
              element: <InterviewConductPage />,
            },
            {
              path: 'interview/:nodeId/transcript/:noteId',
              element: <InterviewTranscriptPage />,
            },
            {
              path: 'mindmap/:mapId/survey/:nodeId/builder',
              element: <SurveyBuilderPage />,
            },
            {
              path: 'mindmap/:mapId/interview/:nodeId/builder',
              element: <InterviewBuilderPage />,
            },
            {
              path: 'settings',
              element: <SettingsPage />,
            },
            {
              path: 'settings/integrations/trello/callback',
              element: <Navigate to="/integrations/trello/callback" replace />,
            },
            {
              path: 'integrations/trello/callback',
              element: <TrelloCallbackPage />,
            },
          ],
        },
        // Survey page - outside RequireSurvey to avoid circular redirect
        {
          path: '/survey',
          element: (
            <RequireAuth>
              <SurveyPage />
            </RequireAuth>
          ),
        },
        // Upgrade page - outside onboarding check so new users can access it
        {
          path: '/upgrade',
          element: (
            <RequireAuth>
              <UpgradePage />
            </RequireAuth>
          ),
        },
        // Upgrade success - no onboarding check (user comes from Stripe)
        {
          path: '/upgrade/success',
          element: (
            <RequireAuth>
              <UpgradeSuccessPage />
            </RequireAuth>
          ),
        },
        {
          path: '/login',
          element: <LoginPage />,
        },
        {
          path: '/signup',
          element: <LoginPage />,
        },
        {
          path: '/become-partner',
          element: <PartnerSignUpPage />,
        },
        {
          path: '/s/:nodeId',
          element: <SurveyParticipantPage />,
        },
        {
          path: '/view',
          element: <PublicLayout />,
          children: [
            {
              path: ':id',
              element: <PublicMindMapWrapper />,
            },
          ],
        },
        {
          path: '/admin',
          element: (
            <RequireAuth>
              <RequireAdmin>
                <Suspense
                  fallback={
                    <LoadingContainer>
                      <Spin size="large" />
                    </LoadingContainer>
                  }
                >
                  <AdminLayout />
                </Suspense>
              </RequireAdmin>
            </RequireAuth>
          ),
          errorElement: <AdminErrorElement />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Spin />}>
                  <AdminDashboard />
                </Suspense>
              ),
            },
            {
              path: 'users',
              element: (
                <Suspense fallback={<Spin />}>
                  <UsersPage />
                </Suspense>
              ),
            },
            {
              path: 'users/:userId',
              element: (
                <Suspense fallback={<Spin />}>
                  <UserDetailPage />
                </Suspense>
              ),
            },
            {
              path: 'partners',
              element: (
                <Suspense fallback={<Spin />}>
                  <PartnersPage />
                </Suspense>
              ),
            },
            {
              path: 'feedback',
              element: (
                <Suspense fallback={<Spin />}>
                  <FeedbackPage />
                </Suspense>
              ),
            },
            {
              path: 'configurations',
              element: (
                <Suspense fallback={<Spin />}>
                  <ConfigurationsPage />
                </Suspense>
              ),
            },
            {
              path: 'ai-feedback',
              element: (
                <Suspense fallback={<Spin />}>
                  <AIGenerationFeedbackPage />
                </Suspense>
              ),
            },
            {
              path: 'token-averages',
              element: (
                <Suspense fallback={<Spin />}>
                  <TokenAveragesPage />
                </Suspense>
              ),
            },
            {
              path: 'credit-system',
              element: (
                <Suspense fallback={<Spin />}>
                  <CreditSystemPage />
                </Suspense>
              ),
            },
            {
              path: 'feature-toggles',
              element: (
                <Suspense fallback={<Spin />}>
                  <FeatureTogglesPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: '/partner/pending',
          element: (
            <RequireAuth>
              <RequirePartner requireApproved={false}>
                <Suspense
                  fallback={
                    <LoadingContainer>
                      <Spin size="large" />
                    </LoadingContainer>
                  }
                >
                  <PartnerApplicationPending />
                </Suspense>
              </RequirePartner>
            </RequireAuth>
          ),
        },
        {
          path: '/partner',
          element: (
            <RequireAuth>
              <RequirePartner>
                <Suspense
                  fallback={
                    <LoadingContainer>
                      <Spin size="large" />
                    </LoadingContainer>
                  }
                >
                  <PartnerLayout />
                </Suspense>
              </RequirePartner>
            </RequireAuth>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Spin />}>
                  <PartnerDashboard />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: '/terms',
          element: <TermsPage />,
        },
        {
          path: '/privacy',
          element: <PrivacyPage />,
        },
        {
          path: '/partner-terms',
          element: <PartnerTermsPage />,
        },
        {
          path: '*',
          element: <NotFoundPage />,
        },
      ],
    },
  ],
  {
    basename: '/app', // Set base path for React Router
  }
)

// Inner app that consumes theme context
function ThemedApp() {
  const { theme: currentTheme } = useThemeContext()
  const themeConfig = useMemo(() => getThemeConfig(currentTheme === 'dark'), [currentTheme])

  return (
    <ConfigProvider theme={themeConfig}>
      <AntApp>
        <AuthProvider>
          <ProfileProvider>
            <FeatureAccessProvider>
              <IntegrationProvider>
                <NotificationProvider>
                  <RouterProvider router={router} />
                </NotificationProvider>
              </IntegrationProvider>
            </FeatureAccessProvider>
          </ProfileProvider>
        </AuthProvider>
      </AntApp>
    </ConfigProvider>
  )
}

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  )
}

export default App
